import router from '../../../router/routes';
import getEditorData from '../../Common/getEditorData';
import saveEditorData from '../../Common/saveEditorData';
import destroyEditorData from '../../Common/destroyEditorData';
import loadCachedFonts from '@/store/Common/GoogleFonts/loadCachedFonts.js';

const defaultPairings = {
    body: {
        'key': 'body',
        'name': 'Body',
        'order': 0,
        'family': 'Open Sans',
        'variant': 'regular',
        'all_caps': false,
        'preview_size': 16,
        'preview_line_height': 1,
        'preview_letter_spacing': 0,
        'selector_size': 16,
        'selector_line_height': 1.4,
        'selector_letter_spacing': 0,
    },

    banner: {
        'key': 'banner',
        'name': 'Banner',
        'order': 0,
        'family': 'Open Sans',
        'variant': 'regular',
        'all_caps': false,
        'preview_size': 16,
        'preview_line_height': 1,
        'preview_letter_spacing': 0,
        'selector_size': 16,
        'selector_line_height': 1.4,
        'selector_letter_spacing': 0,
    },

    button: {
        'key': 'button',
        'name': 'Button',
        'order': 0,
        'family': 'Open Sans',
        'variant': 'regular',
        'all_caps': false,
        'preview_size': 16,
        'preview_line_height': 1,
        'preview_letter_spacing': 0,
        'selector_size': 16,
        'selector_line_height': 1.4,
        'selector_letter_spacing': 0,
    },

    h1: {
        'key': 'h1',
        'name': 'Heading 1',
        'order': 0,
        'family': 'Open Sans',
        'variant': 'regular',
        'all_caps': false,
        'preview_size': 16,
        'preview_line_height': 1,
        'preview_letter_spacing': 0,
        'selector_size': 16,
        'selector_line_height': 1.4,
        'selector_letter_spacing': 0,
    },

    h2: {
        'key': 'h2',
        'name': 'Heading 2',
        'order': 0,
        'family': 'Open Sans',
        'variant': 'regular',
        'all_caps': false,
        'preview_size': 16,
        'preview_line_height': 1,
        'preview_letter_spacing': 0,
        'selector_size': 16,
        'selector_line_height': 1.4,
        'selector_letter_spacing': 0,
    },

    subhead: {
        'key': 'subhead',
        'name': 'Subhead',
        'order': 0,
        'family': 'Open Sans',
        'variant': 'regular',
        'all_caps': false,
        'preview_size': 16,
        'preview_line_height': 1,
        'preview_letter_spacing': 0,
        'selector_size': 16,
        'selector_line_height': 1.4,
        'selector_letter_spacing': 0,
    }
};

const defaultState = () => {
    return {
        fontPairingId: undefined,
        name: undefined,
        deleteModal: false,
        order: 0,
        published: 0,
        route: undefined,
        fonts: [],
        pairings: [],
        postData: [],
        fontPreviewStyle: {
            h1: '',
            h1Swatch: '',
            h2: '',
            subhead: '',
            button: '',
            banner: '',
            body: '',
            bodySwatch: ''
        }
    };
};

const state = defaultState();

export default {
    namespaced: true,
    state,

    getters: {
        h1Style(state) {
            return state.fontPreviewStyle.h1;
        },

        h1SwatchStyle(state) {
            return state.fontPreviewStyle.h1Swatch;
        },

        h2Style(state) {
            return state.fontPreviewStyle.h2;
        },

        subheadStyle(state) {
            return state.fontPreviewStyle.subhead;
        },

        buttonStyle(state) {
            return state.fontPreviewStyle.button;
        },

        bodyStyle(state) {
            return state.fontPreviewStyle.body;
        },

        bodySwatchStyle(state) {
            return state.fontPreviewStyle.bodySwatch;
        },

        bannerStyle(state) {
            return state.fontPreviewStyle.banner;
        },

        postData(state) {
            return {
                name: state.name,
                order: state.order,
                published: state.published,
                pairing: state.pairings
            };
        },

        publishedText(state) {
            if (state.published) {
                return 'Published';
            } else {
                return 'Not Published';
            }
        },

        route(state) {
            if (state.fontPairingId) {
                return '/api/font-pairing/edit/' + state.fontPairingId;
            } else {
                return '/api/font-pairing/create';
            }
        }
    },

    mutations: {
        setFontPairingId(state, value) {
            state.fontPairingId = value;
        },

        setName(state, value) {
            state.name = value;
        },

        setOrder(state, value) {
            state.order = value;
        },

        setPublished(state, value) {
            state.published = value;
        },

        toggleDeleteModal(state, value) {
            state.deleteModal = value;
        },

        setFonts(state, value) {
            state.fonts = value;
        },

        setPairings(state, value) {
            state.pairings = Object.keys(value)
                .sort()
                .reduce(function(result, key) {
                    result[key] = value[key];
                    return result;
                }, {});
        },

        setFontPreviewStyle(state, value) {
            state.fontPreviewStyle[value.key] = value.style;
        }
    },

    actions: {
        resetState({ state }) {
            return new Promise(resolve => {
                Object.assign(state, defaultState());
                resolve();
            });
        },

        getData({ commit, getters, state }) {
            commit('overlay/toggle', true, { root: true });
            commit('setFontPairingId', router.currentRoute.params.id ? router.currentRoute.params.id : 0);

            if (state.fontPairingId) {
                getEditorData(getters.route).then((response) => {
                    const pairing = response.data.data.pairing;
                    commit('setName', pairing.name);
                    commit('setOrder', pairing.order);
                    commit('setPublished', pairing.published);
                    commit('setPairings', pairing.pairing ? pairing.pairing : defaultPairings);
                    commit('overlay/toggle', false, { root: true });
                });
            } else {
                commit('setName', undefined);
                commit('setOrder', 0);
                commit('setPublished', 0);
                commit('setPairings', defaultPairings);
                commit('overlay/toggle', false, { root: true });
            }
        },

        setSinglePair({ state }, value) {
            state.pairings[value.id].family = value.family;
            state.pairings[value.id].variant = value.variant;
            state.pairings[value.id].all_caps = value.all_caps;
            state.pairings[value.id].preview_size = value.preview_size;
            state.pairings[value.id].preview_line_height = value.preview_line_height;
            state.pairings[value.id].preview_letter_spacing = value.preview_letter_spacing;
            state.pairings[value.id].selector_size = value.selector_size;
            state.pairings[value.id].selector_line_height = value.selector_line_height;
            state.pairings[value.id].selector_letter_spacing = value.selector_letter_spacing;
        },

        saveData({ getters, commit, dispatch }) {
            saveEditorData('pairing', getters.route, getters.postData)
                .then((fontPairingId) => {
                    if (router.currentRoute.name === 'FontPairingEdit') {
                        dispatch('sidebar/getSidebar', '', { root: true })
                            .then(() => {
                                commit('overlay/toggle', false, { root: true });
                            });
                    } else {
                        router.push('/font-pairing/edit/' + fontPairingId);
                    }
                })
                .catch(() => {
                });
        },

        destroyData({ state }) {
            destroyEditorData('/api/font-pairing/delete/' + state.fontPairingId)
                .then(() => {
                })
                .catch(() => {
                });
        },

        getFonts({ commit }) {
            return new Promise(resolve => {
                // Check for cached fonts.
                loadCachedFonts()
                    .then(response => {
                        commit('setFonts', response);
                        resolve();
                    });
            });
        }
    }
};
